import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Thin client for the admin-api (glplus-admin-api). Attaches the Cognito id
 * token as a Bearer credential; the admin-api re-verifies it + checks the
 * caller's capability server-side. Self-contained (no shared-libs coupling) so
 * the portal can ship independently of the search-API config plumbing.
 */
const BASE = import.meta.env.VITE_ADMIN_API_URL as string | undefined;

export class AdminApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** A stable reason some routes send with a refusal. */
    public readonly code?: string,
    /** The rest of the refusal's body, when the route sends more than a message. */
    public readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!BASE) throw new AdminApiError('VITE_ADMIN_API_URL is not configured', 0);
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const body = (payload as ({ error?: string; code?: string } & Record<string, unknown>) | null) ?? {};
    throw new AdminApiError(body.error ?? `Request failed (${res.status})`, res.status, body.code, body);
  }
  return (payload as { data: T }).data;
}

export const adminApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

// ─── Typed endpoints ─────────────────────────────────────────────────────────

/** The four apps a staff member can be scoped to, and the per-app access levels. */
export type AppName = 'consumer' | 'vendor' | 'driver' | 'admin';
export type AccessLevel = 'none' | 'view' | 'manage' | 'admin';
export type AppLevels = Record<AppName, AccessLevel>;
export const APP_NAMES: AppName[] = ['consumer', 'vendor', 'driver', 'admin'];
export const ACCESS_LEVELS: AccessLevel[] = ['none', 'view', 'manage', 'admin'];

export interface MeCapabilities {
  features?:{productEvidenceReview?:boolean};
  sub: string;
  email: string;
  /** 'admin' for any staff actor (derived from access, not token claims). */
  role: 'admin';
  groups: string[];
  /** Per-app access levels resolved from the staffer's tblglpemployees row. */
  levels: AppLevels;
  capabilities: string[];
}

/** A GL Plus employee row (staff:manage). */
export interface StaffMember {
  id: string;
  cognitoSub: string;
  email: string;
  name: string | null;
  status: 'active' | 'disabled';
  levels: AppLevels;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssignStaffInput {
  email: string;
  /** Required — go on the employee-pool Cognito invite (given/family name). */
  firstName: string;
  lastName: string;
  levels: AppLevels;
  notes?: string;
  /** Required (must equal email) when granting Admin level on the Admin app. */
  confirm?: string;
}

export interface UpdateStaffInput {
  levels?: Partial<AppLevels>;
  status?: 'active' | 'disabled';
  notes?: string | null;
  confirm?: string;
}

export interface MissingUpcProduct {
  glpProductId: string;
  itemName: string;
  brand: string | null;
  descriptionShort: string | null;
  primaryImageUrl: string | null;
  netWeightMg: number | null;
  // NOTE: UPC is never sent to the client (feedback_never_show_upc) — no upc12/upc13 here.
}

export interface MissingUpcSuggestion {
  kind: 'suggestion';
  // The suggested UPC value is NEVER sent to the client; we only know one exists.
  hasUpc: boolean;
  size?: string;
  imageUrl?: string;
  by: string;
  at: string;
}

export interface MissingManufacturerRow {
  id: string;
  manufacturer: string;
  brand: string | null;
  productCount: number;
  lineCount: number;
  exampleGlpProductIds: string[];
  fdcPresent: boolean;
  sourceUrl: string | null;
  sourceKind: string;
  status: string;
  notes: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

export interface MissingUpcItem {
  id: string;
  glpProductId: string | null;
  source: string;
  attemptCount: number;
  reviewCompleted: boolean;
  suggestion: MissingUpcSuggestion | null;
  product: MissingUpcProduct | null;
  createdAt: string;
}

// ── UPC review queue (never-show-upc: masked last-4 tails + external evidence) ──
export interface UpcReviewHit {
  source: string;
  title: string | null;
}

export interface UpcReviewProduct {
  glpProductId: string;
  itemName: string;
  brand: string | null;
  primaryThumbUrl: string | null;
  primaryImageUrl: string | null;
  glpDepartment: string;
  glpCategory: string;
}

export interface UpcReviewItem {
  glpProductId: string;
  classification: string;
  origin: 'tier2-probe' | 'tier1-no-provenance';
  probedAt: string | null;
  appliedAt: string | null;
  storedLast4: string | null;
  correctedLast4: string | null;
  storedHit: UpcReviewHit | null;
  correctedHit: UpcReviewHit | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  product: UpcReviewProduct | null;
}

export type UpcReviewDecision =
  | 'confirmed-kroger-form'
  | 'confirmed-true'
  | 'keep-verbatim'
  | 'unrepairable';

export interface UpcReviewListResult {
  status: string;
  count: number;
  classificationCounts: Record<string, number>;
  items: UpcReviewItem[];
}

export interface ResolveInput {
  upc: string;
  size?: string;
  imageUrl?: string;
}

// ─── Audit log ───────────────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  actorSub: string;
  actorRole: string;
  capability: string;
  entityType: string;
  entityId: string | null;
  action: string;
  before: unknown;
  after: unknown;
  createdAt: string;
}

export interface AuditLogFilters {
  actorSub?: string;
  entityType?: string;
  entityId?: string;
  capability?: string;
  action?: string;
  before?: string;
  limit?: number;
}

// ─── Store requests ──────────────────────────────────────────────────────────

export type ProductDataReportStatus = 'open' | 'resolved' | 'dismissed';

export interface ProductDataReportItem {
  reportId: string;
  glpProductId: string;
  productName: string;
  brand: string | null;
  thumbUrl: string | null;
  glpUserId: string | null;
  storeId: string | null;
  storeName: string | null;
  storeCity: string | null;
  fields: string[];
  note: string | null;
  source: string;
  status: ProductDataReportStatus;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export type StoreRequestStatus = 'new' | 'reviewing' | 'fulfilled' | 'declined';

export interface StoreRequestItem {
  requestId: string;
  chainId: string | null;
  chainName: string | null;
  glpUserId: string | null;
  specificStoreName: string | null;
  specificStoreAddress: string | null;
  sourceType: string | null;
  notifyEmail: string | null;
  postalCode: string | null;
  status: string;
  requestedAt: string;
}

export interface StoreRequestSummaryRow {
  chainId: string | null;
  chainName: string | null;
  specificStoreName: string | null;
  count: number;
}

// ─── Recipe moderation ───────────────────────────────────────────────────────

export type RecipeStatusFilter = 'published' | 'unpublished' | 'all';

export interface RecipeListItem {
  recipeId: string;
  glpRecipeId: string | null;
  title: string;
  source: string;
  submittedBy: string | null;
  isPublished: boolean;
  isApproved: boolean;
  isFeatured: boolean;
  cuisineTypes: string[];
  mealTypes: string[];
  dishTypes: string[];
  imageThumbUrl: string | null;
  ratingAvg: number;
  ratingCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeFacet {
  value: string;
  count: number;
}

export interface RecipeListFilters {
  status?: RecipeStatusFilter;
  q?: string;
  source?: string;
  cuisine?: string;
  mealType?: string;
  before?: string;
  limit?: number;
}

export interface ClassifyRecipeInput {
  cuisineTypes?: string[];
  mealTypes?: string[];
}

// ─── Recall console ──────────────────────────────────────────────────────────

export type RecallStatusFilter = 'active' | 'closed' | 'all';

export interface RecallListItem {
  recallId: string;
  title: string;
  source: string;
  recallNumber: string | null;
  classification: string | null;
  recallDate: string | null;
  closedDate: string | null;
  status: 'active' | 'closed';
  linkedProductCount: number;
  createdAt: string;
}

/** A linked product — GLPC handle + name only, never a UPC (feedback_never_show_upc). */
export interface RecallLinkProduct {
  glpProductId: string;
  itemName: string;
  brand: string | null;
  primaryThumbUrl: string | null;
}

export interface RecallLink {
  glpProductId: string;
  isActive: boolean;
  linkedAt: string;
  product: RecallLinkProduct | null;
}

export interface RecallDetail {
  recall: {
    recallId: string;
    title: string;
    source: string;
    recallNumber: string | null;
    classification: string | null;
    reason: string | null;
    recallDate: string | null;
    closedDate: string | null;
    status: 'active' | 'closed';
    summary: string | null;
    establishment: string | null;
    states: string[];
    createdAt: string;
  };
  links: RecallLink[];
}

export interface RecallListFilters {
  status?: RecallStatusFilter;
  q?: string;
  source?: string;
  classification?: string;
  before?: string;
  limit?: number;
}

// ─── User management ─────────────────────────────────────────────────────────

export type PortalRole = 'consumer' | 'driver' | 'vendor' | 'admin';
export type UserStatusFilter = 'active' | 'unverified' | 'deleted' | 'all';

export interface UserListItem {
  glpUserId: string;
  email: string;
  firstname: string | null;
  lastname: string | null;
  displayName: string | null;
  role: PortalRole;
  status: string;
  subscriptionTier: string;
  lastSeenAt: string | null;
  createdAt: string;
}

export interface UserDetail extends UserListItem {
  cognitoSub: string;
  emailVerified: boolean;
  phoneE164: string | null;
  phoneVerified: boolean;
  postalCode: string | null;
  homeState: string | null;
  deletedAt: string | null;
  purgeAt: string | null;
  updatedAt: string;
}

export interface MembershipState { tier: string; expiresAt: string | null; source: string | null; hasExternalBilling: boolean }

export interface CognitoState {
  enabled: boolean;
  status: string | null;
}

export interface UserListFilters {
  q?: string;
  role?: PortalRole;
  status?: UserStatusFilter;
  before?: string;
  limit?: number;
}

export interface CreateUserInput {
  email: string;
  role?: PortalRole;
  firstname?: string;
  lastname?: string;
}

export interface UpdateUserInput {
  firstname?: string | null;
  lastname?: string | null;
  displayName?: string | null;
}

// ─── FAQ management ──────────────────────────────────────────────────────────

export interface FaqItem {
  id: string;
  topic: string;
  question: string;
  answer: string;
  questionEs: string | null;
  answerEs: string | null;
  displayOrder: number | null;
  isDisplayed: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type FaqVisibility = 'all' | 'shown' | 'hidden';

export interface FaqListFilters {
  q?: string;
  topic?: string;
  visibility?: FaqVisibility;
  limit?: number;
}

export interface CreateFaqInput {
  topic: string;
  question: string;
  answer: string;
  questionEs?: string | null;
  answerEs?: string | null;
  displayOrder?: number;
  isDisplayed?: boolean;
}

export interface UpdateFaqInput {
  topic?: string;
  question?: string;
  answer?: string;
  questionEs?: string | null;
  answerEs?: string | null;
  displayOrder?: number | null;
  isDisplayed?: boolean;
}

// ─── Support / contact requests ──────────────────────────────────────────────

export type SupportStatus = 'open' | 'resolved' | 'all';

export interface SupportRequestItem {
  id: string;
  topic: string;
  topicOther: string | null;
  question: string;
  answer: string | null;
  submittedBy: string | null;
  submittedEmail: string;
  submittedAt: string;
  isResolved: boolean;
  answeredBy: string | null;
  answeredAt: string | null;
  githubIssueNumber: number | null;
  githubIssueUrl: string | null;
  createdAt: string;
}

export interface SupportSubmitter {
  glpUserId: string;
  email: string;
  displayName: string | null;
  status: string;
}

export interface SupportListFilters {
  status?: SupportStatus;
  q?: string;
  topic?: string;
  before?: string;
  limit?: number;
}

// ─── Mailing list (marketing subscribers) ────────────────────────────────────

export type MailingChannel = 'email' | 'sms' | 'push';

export interface MailingSummary {
  total: number;
  email: number;
  sms: number;
  push: number;
  doNotSell: number;
}

export interface SubscriberItem {
  glpUserId: string;
  email: string | null;
  name: string | null;
  optInAt: string | null;
}

export interface SubscriberListFilters {
  channel?: MailingChannel;
  q?: string;
  after?: string;
  limit?: number;
}

// ─── Product editor + dedup ──────────────────────────────────────────────────
// 🔒 The server never sends a full UPC/EAN. List rows carry no codes; the
// detail carries only masked last-4 (feedback_never_show_upc).

export interface ProductListItem {
  glpProductId: string;
  missingFields: string[];
  readyToComplete: boolean;
  enrichmentExhaustedAt: string | null;
  reviewCompleted: boolean;
  hasAllProductData: boolean;
  itemName: string;
  brand: string | null;
  glpDepartment: string;
  glpCategory: string;
  netWeightMg: number | null;
  netCount: string | null;
  uomImperial: string | null;
  unitSizeImperial: string | null;
  primaryThumbUrl: string | null;
  hasActiveRecall: boolean;
  isSnapEligible: boolean;
  isEdible: boolean;
  hasAllImages: boolean;
  /** See ProductListFilters.origin. Null = provenance unknown (honest default). */
  originSource: string | null;
  variantGroupId: string | null;
  updatedAt: string;
}

export interface ProductDetail {
  evidenceManaged?: boolean;
  missingFields: string[];
  readyToComplete: boolean;
  enrichmentExhaustedAt: string | null;
  expectedVersion: string;
  ingredientsText: string | null;
  ingredientsSource: string | null;
  descriptionSource: string | null;
  primaryImageSource: string | null;
  primaryImageUrl: string | null;
  nutritionSource: string | null;
  nutritionDataCompleted: boolean;
  nutrition: { nutrients?: Record<string, number> } | null;
  reviewHistory: Array<{ at: string; by: string; evidence: string }>;

  glpProductId: string;
  itemName: string;
  itemNameEs: string | null;
  brand: string | null;
  manufacturer: string | null;
  descriptionShort: string | null;
  descriptionLong: string | null;
  glpDepartment: string;
  glpCategory: string;
  glpSubCategory: string | null;
  conceptId: string | null;
  productTags: string[];
  isSnapEligible: boolean;
  isAlcoholic: boolean;
  isEdible: boolean;
  isSupplement: boolean;
  currencyCode: string;
  unitSizeImperial: string | null;
  uomImperial: string | null;
  unitSizeMetric: string | null;
  uomMetric: string | null;
  netWeightMg: number | null;
  netCount: string | null;
  hasActiveRecall: boolean;
  variantGroupId: string | null;
  hasAllProductData: boolean;
  hasAllNutrition: boolean;
  hasAllImages: boolean;
  reviewCompleted: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  primaryThumbUrl: string | null;
  createdAt: string;
  updatedAt: string;
  /** Masked last-4 only — never the full code (feedback_never_show_upc). */
  upcLast4: string | null;
  eanLast4: string | null;
}

export interface ProductListFilters {
  worklist?: 'all' | 'incomplete' | 'exhausted';
  afterId?: string;
  q?: string;
  department?: string;
  /**
   * Row provenance (migration 0152 `origin_source`). `origin: 'food_recall'`
   * IS the recall-stub bin — rows recallSync had to mint so a recall could be
   * matched against inventories, which no retailer carries and staff must
   * complete by hand.
   */
  origin?: string;
  before?: string;
  limit?: number;
}

export interface UpdateProductInput {
  expectedVersion?: string;
  ingredientsText?: string | null;
  nutritionPanel?: { energyKcal: number; proteinG: number; fatG: number; carbohydrateG: number };
  reviewEvidence?: string;
  markReviewed?: boolean;
  itemName?: string;
  itemNameEs?: string | null;
  brand?: string | null;
  manufacturer?: string | null;
  descriptionShort?: string | null;
  descriptionLong?: string | null;
  glpDepartment?: string;
  glpCategory?: string;
  glpSubCategory?: string | null;
  productTags?: string[];
  isSnapEligible?: boolean;
  isAlcoholic?: boolean;
  isEdible?: boolean;
  isSupplement?: boolean;
}

export type VariantCandidateStatus = 'pending' | 'needs_review' | 'confirmed' | 'rejected' | 'all';

export interface VariantCandidateItem {
  id: string;
  sourceGlpProductId: string;
  candidateTitle: string | null;
  candidateBrand: string | null;
  candidateSize: string | null;
  /** Masked last-4 only (feedback_never_show_upc). */
  candidateUpcLast4: string | null;
  reconciledGlpProductId: string | null;
  source: string;
  status: string;
  notes: string | null;
  discoveredAt: string;
  sourceProduct: {
    glpProductId: string;
    itemName: string;
    brand: string | null;
    glpDepartment: string;
  } | null;
}

function qs(params: Record<string, string | number | undefined>): string {
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return pairs.length ? `?${pairs.join('&')}` : '';
}

// ── Background-job runner (job:run) ──
export interface JobLastRun {
  status: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  triggeredBy: string | null;
  triggerSource: string;
}
export interface JobCatalogItem {
  name: string;
  domain: string;
  schedule: 'cron' | 'on-demand';
  destructive: boolean;
  description: string;
  lastStatus: string;
  lastRun: JobLastRun | null;
}
export interface JobsListResult {
  count: number;
  total: number;
  items: JobCatalogItem[];
  domainCounts: Record<string, number>;
}
export interface JobRunItem {
  id: string;
  status: string;
  triggerSource: string;
  triggeredBy: string | null;
  summary: unknown;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
}
export interface JobRunsResult {
  job: string;
  count: number;
  items: JobRunItem[];
  nextCursor: string | null;
}
export interface GlobalJobRunItem extends JobRunItem {
  jobName: string;
}
export interface AllRunsResult {
  count: number;
  items: GlobalJobRunItem[];
  nextCursor: string | null;
}
export interface RunJobResult {
  job: string;
  invokeStatus: number;
  functionError: string | null;
  result: unknown;
}
export interface JobsListFilters {
  q?: string;
  domain?: string;
  schedule?: 'cron' | 'on-demand';
  status?: 'success' | 'failure' | 'running' | 'never';
}


/** A row in the product-image sourcing queue. */
export interface ProductImageQueueItem {
  evidenceRevision?: number;
  pendingEvidenceReview?: boolean;
  glpProductId: string;
  itemName: string;
  brand: string | null;
  upc12: string | null;
  glpDepartment: string;
  imageFindState: string | null;
  imageFindAttempts: number | null;
  imageFindLastAt: string | null;
  sourceImageUrl: string | null;
  /** A URL is already queued and waiting for the nightly rehost sweep. */
  pendingRehost: boolean;
}

export interface ProductImageQueueStats {
  notFound: number;
  unattempted: number;
  pendingRehost: number;
}

export type ProductImageQueueState = 'not_found' | 'unattempted' | 'all';

// ─── Runner applications (driver app; admin-api proxies runner-api) ──────────
export type RunnerAppStatus =
  | 'submitted' | 'in_review' | 'declined_prescreen' | 'checkr_pending' | 'checkr_clear'
  | 'checkr_waived'
  | 'checkr_consider' | 'declined_checkr' | 'profile_setup' | 'insurance_review' | 'banking_tax' | 'training_ack'
  | 'verification_call' | 'shadow_ride' | 'pre_golive_review' | 'approved' | 'live' | 'withdrawn';
export type RunnerAppFilter = RunnerAppStatus | 'all' | 'open';
export interface RunnerApplicationItem {
  id: string;
  cognitoSub: string | null;
  email: string;
  phoneE164: string | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  metro: string;
  zonesInterested: string[];
  transportMode: string;
  status: RunnerAppStatus;
  source: string;
  recruitCodeId: string | null;
  priorRelationshipAttested: boolean;
  priorRelationshipNote: string | null;
  checkrCandidateId: string | null;
  checkrResult: string | null;
  checkrCompletedAt: string | null;
  /** Checkr SMB display state — see CHECKR_STATUS_LABEL. */
  checkrStatus: string | null;
  checkrReportId: string | null;
  /** Deep link to the report in Checkr's own dashboard (adjudicators only). */
  checkrReportUrl: string | null;
  /**
   * 🛑 Set iff the background check was WAIVED — no consumer report exists.
   * Wherever checkrResult is rendered, this must be rendered too: a waived
   * applicant that looks identical to a cleared one is the failure this feature
   * has to avoid.
   */
  checkrWaiverId: string | null;
  checkrWaiver: {
    code: string; reason: string; note: string | null;
    mintedBy: string; consumedBy: string | null; consumedAt: string | null;
  } | null;
  idvStatus: string | null;
  prescreenDecidedBy: string | null;
  prescreenDecidedAt: string | null;
  preAdverseSentAt: string | null;
  finalAdverseSentAt: string | null;
  declineReason: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  trainingAckedAt: string | null;
  handbookVersionAcked: string | null;
  shadowRideAt: string | null;
  withdrawnAt: string | null;
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
}
export interface RunnerApplicationDetail extends RunnerApplicationItem {
  recruitCode: { code: string; note: string | null; consumedAt: string | null } | null;
  profile: { id: string; glpdId: string | null; lifecycle: string } | null;
}
export interface RunnerReviewBrief {
  id: string;
  stage: string;
  brief: { note?: string; summary?: string; flags?: string[] };
  decision: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  generatedAt: string;
}
export interface RunnerAppListFilters {
  status?: RunnerAppFilter;
  metro?: 'charlotte' | 'cincinnati';
  q?: string;
  limit?: number;
}
export interface RunnerAppListResult {
  applications: RunnerApplicationItem[];
  counts: Record<string, number>;
  nextBefore: string | null;
}
export type RunnerTransitionAction =
  | 'start_review' | 'proceed_to_checkr' | 'checkr_clear' | 'checkr_consider' | 'advance'
  | 'decline' | 'go_live' | 'waive_checkr';
export interface RunnerTransitionBody {
  action: RunnerTransitionAction;
  reason?: string;
  note?: string;
  /** Required by waive_checkr — the code IS the authorisation. */
  waiverCode?: string;
}

export const WAIVER_REASONS = [
  { value: 'known_to_founder', label: 'Known to the founder' },
  { value: 'staff_test_account', label: 'Internal staff test account' },
  { value: 'launch_pilot', label: 'Launch pilot' },
  { value: 'previously_cleared', label: 'Cleared a check with us before' },
  { value: 'other', label: 'Other (explain in the note)' },
] as const;

export interface RunnerCheckrWaiver {
  id: string;
  code: string;
  reason: string;
  note: string | null;
  mintedBy: string;
  mintedAt: string;
  expiresAt: string | null;
  consumedBy: string | null;
  consumedAt: string | null;
  applications: { id: string; firstName: string; lastName: string; status: string }[];
}
export interface RunnerRecruitCode {
  id: string;
  code: string;
  note: string | null;
  mintedBy: string;
  mintedAt: string;
  expiresAt: string | null;
  consumedAt: string | null;
  applications: { id: string; firstName: string; lastName: string; status: string }[];
}

export type RunnerPhotoKind = 'profile' | 'vehicle';
export type RunnerPhotoStatus = 'pending' | 'live' | 'rejected' | null;

export interface RunnerPhotoQueueItem {
  id: string;
  glpdId: string | null;
  firstName: string;
  lastName: string;
  metro: string;
  lifecycle: string;
  photoStatus: RunnerPhotoStatus;
  photoRejectionReason: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleColor: string | null;
  vehiclePhotoStatus: RunnerPhotoStatus;
  updatedAt: string;
  /** Presigned, minted per response — never stored, never public. */
  submittedPhotoViewUrl: string | null;
  livePhotoViewUrl: string | null;
  submittedVehicleViewUrl: string | null;
}

export interface RunnerPhotoQueueResult {
  items: RunnerPhotoQueueItem[];
  counts: { profile: number; vehicle: number };
}

export interface RunnerPhotoReviewBody {
  kind: RunnerPhotoKind;
  decision: 'approve' | 'reject';
  reason?: string;
}


/** One row in the recipe-comment moderation queue. */
export interface CommentQueueItem {
  commentId: string;
  author: string;
  body: string;
  rating: number | null;
  status: 'pending' | 'approved' | 'rejected';
  reportCount: number;
  reasons: string[];
  recipeId: string | null;
  recipeTitle: string | null;
  createdAt: string;
}

/**
 * 🛑 `reported` is a LENS, not a status — rows carrying a report whose status is
 * pending OR approved. A report on an already-approved comment is the case a
 * moderator most needs to see.
 */
export type CommentQueueFilter = 'pending' | 'reported' | 'approved' | 'rejected';

export interface CommentQueueResult {
  comments: CommentQueueItem[];
  nextCursor: string | null;
  counts: { pending: number; approved: number; rejected: number; reported: number };
}

// ── Runner insurance + video call (2026-10-01 onboarding order) ──
export type RunnerInsuranceStatus = 'pending' | 'approved' | 'rejected' | 'superseded';
export interface RunnerInsurancePolicy {
  id: string;
  insurer: string;
  policyNumber: string;
  expiresOn: string;
  daysLeft: number;
  status: RunnerInsuranceStatus;
  submittedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectReason: string | null;
  /** Presigned, short-lived; the bucket is private. */
  cardViewUrl: string | null;
  runner: { id: string; firstName: string; lastName: string; email: string; metro: string; lifecycle: string; glpdId: string | null; applicationId: string | null };
}
export type RunnerCallStatus = 'scheduled' | 'completed' | 'canceled' | 'rescheduled' | 'no_show';
export interface RunnerCallCounts { booked: number; rescheduled: number; canceled: number; noShow: number; completed: number }
export interface RunnerVerificationCall {
  id: string;
  applicationId: string;
  status: RunnerCallStatus;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  joinUrl: string | null;
  interviewerName: string | null;
  completedAt: string | null;
  createdAt: string;
  application: { firstName: string; lastName: string; email: string; metro: string; status: RunnerAppStatus };
  counts: RunnerCallCounts;
}
export interface RunnerCallEvent { id: string; kind: string; actor: string | null; detail: Record<string, unknown> | null; createdAt: string }

// ── Runner capacity limits (runner:read view · runner:limits:review decide/override) ──
export type RunnerLimitStage = 'starting' | 'trusted' | 'trusted90';
export interface RunnerLimitRequestRow {
  id: string; kind: 'deliveries' | 'households' | 'both'; requestedDeliveries: number | null; requestedHouseholds: number | null; note: string | null;
  status: 'pending' | 'approved' | 'declined'; decidedAt: string | null; decisionReason: string | null;
  grantedDeliveries: number | null; grantedHouseholds: number | null; grantedExpiresAt: string | null; coolingUntil: string | null; createdAt: string;
}
export interface RunnerLimitUsage { weeklyCapacity: number; activeHouseholds: number; deliveriesCompleted: number }

/**
 * The household's side of a released run.
 *
 * 🔑 `told` is the record of what this household has ACTUALLY heard, and
 * `money.holdReleased` is the answer to the first question they ask. Both come
 * off the order row — never a reassurance somebody typed.
 */
/** A household's ordering pause, with the record behind it (Rod, 2026-09-27). */
export interface OrderingRecordEventRow {
  id: string;
  kind: 'delivery_day_cancel' | 'delivery_day_move' | 'cancel_after_pickup' | 'not_home' | 'early_move';
  counter: 'late_change' | 'early_move';
  occurredAt: string;
  orderId: string | null;
  detail: { feeCents?: number; feeCoveredBy?: string | null; from?: string; to?: string } | null;
  voided: boolean;
  voidReason: string | null;
}
export interface OrderingReview {
  pauseId: string;
  kind: 'auto_14d' | 'review';
  counter: 'late_change' | 'early_move';
  startedAt: string;
  endsAt: string | null;
  ordersAllTime: number;
  decision: 'reinstated' | 'kept_paused' | 'expired' | null;
  decisionNote: string | null;
  liftedAt: string | null;
  liftedBy: string | null;
  household: { glpUserId: string; name: string | null; email: string | null };
  events: OrderingRecordEventRow[];
  priorPauses: Array<{ pauseId: string; kind: string; counter: string; startedAt: string; endsAt: string | null; liftedAt: string | null; decision: string | null }>;
}

export type OrderReviewOutcome = 'charge_pay' | 'refund_pay' | 'refund_no_pay';

export interface OrderDoorstepEvidence {
  runnerId?: string;
  exceptionCode: string | null;
  exceptionNote: string | null;
  doorWaitStartedAt: string | null;
  doorWaitLocation: boolean;
  doorReply: 'leave' | 'coming' | null;
  doorReplyAt: string | null;
  retryPendingAt: string | null;
  deliveredAt: string | null;
  deliveryLocation: boolean;
  deliveryPhoto: string | null;
  undeliveredAt: string | null;
  undeliveredReason: 'nobody_home' | 'safety' | null;
  undeliveredLocation: boolean;
  doorPhoto: string | null;
  donatedAt: string | null;
  donationPhoto: string | null;
  donationNote: string | null;
}

export interface OrderReview {
  orderId: string;
  ref: string;
  runnerId: string | null;
  runnerName: string | null;
  runDate: string | null;
  fulfillmentStatus: string;
  kind: 'safety' | 'couldnt_deliver' | 'delivered';
  reportedAt: string | null;
  holdExpiresAt: string | null;
  reportReason: string | null;
  reportNote: string | null;
  undeliveredReason: string | null;
  heldCents: number;
  tipCents: number;
  reviewStatus: 'open' | 'resolved' | null;
  reviewOutcome: OrderReviewOutcome | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  household: { glpUserId: string; name: string | null; email: string | null };
  evidence: OrderDoorstepEvidence | null;
  runnerStats: { delivered: number; couldNotDeliver: number; safetyExits: number; total: number; couldNotDeliverRate: number } | null;
}

export interface OrderNeedingCover {
  orderId: string;
  orderRef: string;
  fulfillmentStatus: string;
  cancelReason: string | null;
  cancelledAt: string | null;
  runDate: string | null;
  deliveryWindowStart: string | null;
  deliveryWindowEnd: string | null;
  runnerDisplayName: string | null;
  household: { glpUserId: string; name: string | null; email: string | null };
  money: { estimatedTotalCents: number; authorizedCents: number; capturedCents: number; holdReleased: boolean };
  told: { runnerUnavailableAt: string | null; coveredAt: string | null; cancelledAt: string | null };
}

/**
 * Where an order was actually shopped (migration 0184).
 *
 * 🪤 `planned: false` is the distinction the table exists for — a Runner picked
 * something up somewhere the order never named, because a replacement came from
 * a different store.
 */
export interface OrderLocationRow {
  orderLocationId: string;
  storeId: string;
  storeName: string | null;
  where: string | null;
  vendorId: string | null;
  vendorName: string | null;
  chainId: string | null;
  chainName: string | null;
  sequence: number;
  planned: boolean;
  status: string;
  runnerId: string | null;
  pickedUpAt: string | null;
  expectedCents: number | null;
  authorizedCents: number | null;
  cardLive: boolean;
  registerTotalCents: number | null;
  registerTaxCents: number | null;
  receiptPhotoUrl: string | null;
  receiptMissing: boolean;
}
export interface OrderLocationsResponse {
  order: { orderId: string; orderRef: string; fulfillmentStatus: string; runDate: string | null };
  locations: OrderLocationRow[];
  summary: { total: number; unplanned: number; pickedUp: number; receiptsMissing: number; registerTotalCents: number };
}

// ─── Cover queue (runs released by a Runner's time off) ──────────────────────
/**
 * 🛑 The marketplace is AUTOMATIC (Rod, 2026-09-12). These rows describe what
 * the system already did — who it offered the run to and what they said. A
 * person widens or names someone; nobody offers the first round by hand.
 */
export interface CoverOfferRecipient {
  runnerId: string;
  name: string | null;
  glpdId: string | null;
  reason: string | null;
  notifiedAt: string | null;
  declinedAt: string | null;
}
export interface CoverOffer {
  id: string;
  status: 'open' | 'covered' | 'expired' | 'withdrawn';
  metro: string;
  zone: string;
  runDate: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
  orderIds: string[];
  orderCount: number;
  expiresAt: string;
  createdAt: string;
  releasedBy: { runnerId: string; name: string | null; glpdId: string | null };
  timeOffId: string | null;
  acceptedBy: { runnerId: string; name: string | null } | null;
  acceptedAt: string | null;
  newRunId: string | null;
  recipients: CoverOfferRecipient[];
  offered: number;
  passed: number;
  noAnswer: number;
}
export interface CoverCandidate {
  runnerId: string;
  name: string | null;
  glpdId: string | null;
  zones: string[];
  /** False means taking it needs the offer WIDENED past the zone. */
  inZone: boolean;
  offeredAt: string | null;
  declinedAt: string | null;
}

export interface RunnerLimitQueueItem extends RunnerLimitRequestRow {
  runner: { id: string; name: string; glpdId: string | null; stage: RunnerLimitStage; limits: { deliveries: number | null; households: number }; usage: RunnerLimitUsage };
}
export interface RunnerLimitRecordPack {
  runner: { id: string; name: string; glpdId: string | null; email: string; transportMode: string; metro: string; wentLiveAt: string | null; trustedTier: string | null; trustedSince: string | null; ratingAvg: number | null; ratingCount: number };
  stage: RunnerLimitStage;
  limits: { deliveries: number | null; households: number; source: { deliveries: 'tier' | 'override'; households: 'tier' | 'override' }; overrideReason: string | null; overrideBy: string | null; overrideAt: string | null; overrideExpiresAt: string | null };
  usage: RunnerLimitUsage;
  reliability: { acceptedNotDelivered: number; exceptions90d: number; householdsEnded90d: number; since30: string };
  money: { varianceFlags: number };
  history: { action: string; by: string | null; at: string; detail: unknown }[];
}
export interface RunnerLimitDecideBody { decision: 'approve' | 'decline'; reason: string; grantedDeliveries?: number | null; grantedHouseholds?: number | null; expiresAt?: string | null; lang?: 'en' | 'es' }
export interface RunnerLimitOverrideBody { deliveries?: number | null; households?: number | null; reason: string; expiresAt?: string | null; lang?: 'en' | 'es' }
export interface PurgeRunnerResult {
purged: boolean;
runnerId: string;
glpdId: string | null;
email: string | null;
removed: { profile: boolean; application: boolean; photos: number; cognitoAccount: boolean };
retained: {
  denylistEntries: string;
  checkrCandidateId: string | null;
  checkrReportId: string | null;
  stripeConnectAccountId: string | null;
  stripeIssuingCardholderId: string | null;
};
}

export const adminApiClient = {
  getCapabilities: () => adminApi.get<MeCapabilities>('/admin/me/capabilities'),

  // ── Recipe comments (comment:read view · comment:moderate write) ──
  listComments: (filters: { status?: CommentQueueFilter; limit?: number; cursor?: string } = {}) =>
    adminApi.get<CommentQueueResult>(`/admin/comments${qs({ ...filters })}`),
  moderateComment: (commentId: string, body: { action: 'approve' | 'reject'; note?: string }) =>
    adminApi.post<{ commentId: string; status: string; moderatedAt: string }>(
      `/admin/comments/${encodeURIComponent(commentId)}/moderate`,
      body,
    ),

  // ── Background jobs ──
  listJobs: (filters: JobsListFilters = {}) =>
    adminApi.get<JobsListResult>(`/admin/jobs${qs({ ...filters })}`),
  getJobRuns: (job: string, limit = 25) =>
    adminApi.get<JobRunsResult>(`/admin/jobs/${encodeURIComponent(job)}/runs${qs({ limit })}`),
  getAllJobRuns: (limit = 30) => adminApi.get<AllRunsResult>(`/admin/job-runs${qs({ limit })}`),
  runJob: (job: string, body: { params?: Record<string, unknown>; confirm?: string }) =>
    adminApi.post<RunJobResult>(`/admin/jobs/${encodeURIComponent(job)}/run`, body),

  // ── Runner applications (runner:read view · runner:review / approve write) ──
  listRunnerApplications: (filters: RunnerAppListFilters = {}) =>
    adminApi.get<RunnerAppListResult>(`/admin/runner-applications${qs({ ...filters })}`),
  getRunnerApplication: (id: string) =>
    adminApi.get<{ application: RunnerApplicationDetail; briefs: RunnerReviewBrief[] }>(
      `/admin/runner-applications/${id}`,
    ),
  transitionRunnerApplication: (id: string, body: RunnerTransitionBody) =>
    adminApi.post<{ application: RunnerApplicationItem; profileId: string | null; glpdId: string | null }>(
      `/admin/runner-applications/${id}/transition`,
      body,
    ),
  // ── Runner photo review (§18c — runner:read view · runner-photo:review/reject write) ──
  listRunnerPhotoQueue: (filters: { kind?: RunnerPhotoKind | 'all'; limit?: number } = {}) =>
    adminApi.get<RunnerPhotoQueueResult>(`/admin/runner-photos${qs({ ...filters })}`),
  reviewRunnerPhoto: (runnerId: string, body: RunnerPhotoReviewBody) =>
    adminApi.post<{ runnerId: string; kind: string; decision: string }>(
      `/admin/runner-photos/${runnerId}/review`,
      body,
    ),
  // ── Runner insurance (runner:read view · runner-insurance:review write) ──
  listRunnerInsurance: (filters: { status?: RunnerInsuranceStatus | 'all'; runnerId?: string; limit?: number } = {}) =>
    adminApi.get<{ items: RunnerInsurancePolicy[]; counts: { pending: number } }>(`/admin/runner-insurance${qs({ ...filters })}`),
  reviewRunnerInsurance: (id: string, body: { decision: 'approve' | 'reject'; reason?: string }) =>
    adminApi.post<{ policyId: string; decision: string; advancedTo: string | null }>(`/admin/runner-insurance/${id}/review`, body),
  // ── Runner video call (runner:read view · runner:review write) ──
  listRunnerVerificationCalls: (filters: { status?: RunnerCallStatus | 'all'; applicationId?: string; limit?: number } = {}) =>
    adminApi.get<{ calls: RunnerVerificationCall[]; events?: RunnerCallEvent[] }>(`/admin/runner-verification-calls${qs({ ...filters })}`),
  completeRunnerVerificationCall: (id: string, body: { interviewerName: string; note?: string }) =>
    adminApi.post<{ callId: string; status: string; counts: RunnerCallCounts }>(`/admin/runner-verification-calls/${id}/complete`, body),
  noShowRunnerVerificationCall: (id: string, body: { note?: string } = {}) =>
    adminApi.post<{ callId: string; status: string; counts: RunnerCallCounts }>(`/admin/runner-verification-calls/${id}/no-show`, body),
  // ── Delete a Runner completely (runner:purge — admin level) ──
  // Hard-deletes the Cognito account too, so the same person can sign up
  // again. runner-api refuses it outright for a Runner who has been paid, and
  // returns the Checkr and Stripe ids it cannot remove.
  purgeRunner: (runnerId: string, body: { acknowledge: 'DELETE-THIS-RUNNER-COMPLETELY'; reason: string }) =>
    adminApi.del<PurgeRunnerResult>(`/admin/runners/${runnerId}`, body),

  // ── Runner capacity limits ──
  getOrderLocations: (orderId: string) =>
    adminApi.get<OrderLocationsResponse>(`/admin/orders/${encodeURIComponent(orderId)}/locations`),
  listOrderingReviews: (state: 'open' | 'auto' | 'decided' = 'open') =>
    adminApi.get<{ reviews: OrderingReview[]; counts: { open: number; auto: number } }>(`/admin/ordering-reviews${qs({ state })}`),
  decideOrderingReview: (pauseId: string, body: { decision: 'reinstated' | 'kept_paused'; note: string }) =>
    adminApi.post<{ pauseId: string; decision: string; liftedAt: string | null }>(`/admin/ordering-reviews/${encodeURIComponent(pauseId)}/decision`, body),
  voidOrderingRecordEvent: (eventId: string, reason: string) =>
    adminApi.post<{ id: string; voided: boolean }>(`/admin/ordering-record/events/${encodeURIComponent(eventId)}/void`, { reason }),
  listOrderReviews: (state: 'open' | 'decided' = 'open') =>
    adminApi.get<{ reviews: OrderReview[]; counts: { open: number } }>(`/admin/order-reviews${qs({ state })}`),
  decideOrderReview: (orderId: string, body: { outcome: OrderReviewOutcome; note: string }) =>
    adminApi.post<{ orderId: string; outcome: OrderReviewOutcome }>(`/admin/order-reviews/${encodeURIComponent(orderId)}/decide`, body),
  listOrdersNeedingCover: (state: 'needs_cover' | 'no_cover' | 'all' = 'needs_cover') =>
    adminApi.get<{ orders: OrderNeedingCover[]; counts: Record<string, number> }>(`/admin/orders/cover${qs({ state })}`),
  listCoverOffers: (status: 'open' | 'covered' | 'expired' | 'all' = 'open') =>
    adminApi.get<{ offers: CoverOffer[]; counts: Record<string, number> }>(`/admin/runner-cover-offers${qs({ status })}`),
  getCoverCandidates: (id: string) =>
    adminApi.get<{ offer: CoverOffer; candidates: CoverCandidate[] }>(`/admin/runner-cover-offers/${encodeURIComponent(id)}/candidates`),
  offerCoverToRunner: (id: string, runnerId: string) =>
    adminApi.post<{ offered: boolean; runnerId: string }>(`/admin/runner-cover-offers/${encodeURIComponent(id)}/offer`, { runnerId }),
  listRunnerLimitRequests: (status: 'pending' | 'approved' | 'declined' | 'all' = 'pending') =>
    adminApi.get<{ requests: RunnerLimitQueueItem[]; counts: Record<string, number> }>(`/admin/runner-limit-requests${qs({ status })}`),
  getRunnerLimitRequest: (id: string) =>
    adminApi.get<{ request: RunnerLimitRequestRow } & RunnerLimitRecordPack>(`/admin/runner-limit-requests/${encodeURIComponent(id)}`),
  decideRunnerLimitRequest: (id: string, body: RunnerLimitDecideBody) =>
    adminApi.post<{ request: RunnerLimitRequestRow | null; limits?: { deliveries: number | null; households: number } }>(`/admin/runner-limit-requests/${encodeURIComponent(id)}/decide`, body),
  getRunnerLimits: (runnerId: string) => adminApi.get<RunnerLimitRecordPack>(`/admin/runners/${encodeURIComponent(runnerId)}/limits`),
  overrideRunnerLimits: (runnerId: string, body: RunnerLimitOverrideBody) =>
    adminApi.post<{ limits: { deliveries: number | null; households: number; stage: RunnerLimitStage } }>(`/admin/runners/${encodeURIComponent(runnerId)}/limits`, body),

  mintRunnerRecruitCode: (body: { note?: string; expiresInDays?: number }) =>
    adminApi.post<{ recruitCode: RunnerRecruitCode }>('/admin/runner-recruit-codes', body),
  listRunnerRecruitCodes: (state: 'unused' | 'consumed' | 'all' = 'unused') =>
    adminApi.get<{ recruitCodes: RunnerRecruitCode[] }>(`/admin/runner-recruit-codes${qs({ state })}`),
  // 🛑 Not the recruit code. This authorises skipping the background check and
  // never goes to the applicant.
  mintRunnerCheckrWaiver: (body: { reason: string; note?: string; expiresInDays?: number }) =>
    adminApi.post<{ checkrWaiver: RunnerCheckrWaiver }>('/admin/runner-checkr-waivers', body),
  listRunnerCheckrWaivers: (state: 'unused' | 'consumed' | 'all' = 'unused') =>
    adminApi.get<{ checkrWaivers: RunnerCheckrWaiver[] }>(`/admin/runner-checkr-waivers${qs({ state })}`),

  // ── Product editor + dedup (product:read view · product:edit write) ──
  listProducts: (filters: ProductListFilters = {}) =>
    adminApi.get<{ count: number; items: ProductListItem[]; nextCursor: string | null; nextId: string | null }>(
      `/admin/products${qs({ ...filters })}`,
    ),
  getProduct: (glpProductId: string) =>
    adminApi.get<{ product: ProductDetail }>(`/admin/products/${encodeURIComponent(glpProductId)}`),
  updateProduct: (glpProductId: string, input: UpdateProductInput) =>
    adminApi.patch<{ product: ProductDetail }>(`/admin/products/${encodeURIComponent(glpProductId)}`, input),
  listVariantCandidates: (filters: { status?: VariantCandidateStatus; q?: string; limit?: number } = {}) =>
    adminApi.get<{ status: string; count: number; items: VariantCandidateItem[] }>(
      `/admin/products/variant-candidates${qs({ ...filters })}`,
    ),
  confirmVariantCandidate: (id: string) =>
    adminApi.post<{ status: string; variantGroupId: string }>(`/admin/products/variant-candidates/${id}/confirm`),
  rejectVariantCandidate: (id: string, notes?: string) =>
    adminApi.post<{ status: string; id: string }>(
      `/admin/products/variant-candidates/${id}/reject`,
      notes ? { notes } : {},
    ),

  listMissingUpc: (status: 'pending' | 'suggested' | 'all' = 'pending') =>
    adminApi.get<{ status: string; count: number; items: MissingUpcItem[] }>(
      `/admin/missing-upc?status=${status}`,
    ),
  resolveMissingUpc: (id: string, input: ResolveInput) =>
    adminApi.post(`/admin/missing-upc/${id}/resolve`, input),
  suggestMissingUpc: (id: string, input: ResolveInput) =>
    adminApi.post(`/admin/missing-upc/${id}/suggest`, input),
  approveMissingUpc: (id: string) => adminApi.post(`/admin/missing-upc/${id}/approve`),
  rejectMissingUpc: (id: string) => adminApi.post(`/admin/missing-upc/${id}/reject`),
  // Manufacturer-source worklist (size-discovery step 3)
  listMissingManufacturers: (status?: string) =>
    adminApi.get<{ rows: MissingManufacturerRow[]; statusCounts: Record<string, number> }>(
      `/admin/missing-manufacturers${status ? `?status=${status}` : ''}`,
    ).then((r) => r),
  updateMissingManufacturer: (id: string, input: Partial<{ status: string; sourceUrl: string | null; sourceKind: string; notes: string | null }>) =>
    adminApi.patch<{ row: MissingManufacturerRow }>(`/admin/missing-manufacturers/${id}`, input),

  listUpcReview: (status: 'pending' | 'reviewed' | 'all' = 'pending', classification?: string) =>
    adminApi.get<UpcReviewListResult>(
      `/admin/upc-review?status=${status}${classification ? `&classification=${encodeURIComponent(classification)}` : ''}&limit=200`,
    ),
  classifyUpcReview: (glpProductId: string, body: { decision: UpcReviewDecision; note?: string }) =>
    adminApi.post<{ status: string; classification: string; appliesOn: string | null }>(
      `/admin/upc-review/${encodeURIComponent(glpProductId)}/classify`,
      body,
    ),

  listAuditLog: (filters: AuditLogFilters = {}) =>
    adminApi.get<{ count: number; items: AuditLogEntry[]; nextCursor: string | null }>(
      `/admin/audit-log${qs({ ...filters })}`,
    ),

  listStoreRequests: (status?: StoreRequestStatus, limit?: number) =>
    adminApi.get<{ view: string; status: string; count: number; items: StoreRequestItem[] }>(
      `/admin/store-requests${qs({ view: 'list', status, limit })}`,
    ),
  storeRequestSummary: (status?: StoreRequestStatus, limit?: number) =>
    adminApi.get<{ view: string; count: number; items: StoreRequestSummaryRow[] }>(
      `/admin/store-requests${qs({ view: 'summary', status, limit })}`,
    ),
  updateStoreRequestStatus: (id: string, status: StoreRequestStatus) =>
    adminApi.post(`/admin/store-requests/${id}/status`, { status }),

  listProductDataReports: (filters: { status?: ProductDataReportStatus; before?: string; limit?: number } = {}) =>
    adminApi.get<{
      status: ProductDataReportStatus;
      count: number;
      openCount: number;
      nextBefore: string | null;
      items: ProductDataReportItem[];
    }>(`/admin/product-data-reports${qs({ ...filters })}`),
  closeProductDataReport: (id: string, status: 'resolved' | 'dismissed', resolutionNote?: string) =>
    adminApi.post(`/admin/product-data-reports/${id}/close`, { status, resolutionNote }),

  listRecipes: (filters: RecipeListFilters = {}) =>
    adminApi.get<{
      status: string;
      count: number;
      items: RecipeListItem[];
      nextCursor: string | null;
    }>(`/admin/recipes${qs({ ...filters })}`),
  recipeFacets: () =>
    adminApi.get<{ cuisineTypes: RecipeFacet[]; mealTypes: RecipeFacet[] }>(
      '/admin/recipes/facets',
    ),
  publishRecipe: (id: string) => adminApi.post(`/admin/recipes/${id}/publish`),
  unpublishRecipe: (id: string) => adminApi.post(`/admin/recipes/${id}/unpublish`),
  classifyRecipe: (id: string, input: ClassifyRecipeInput) =>
    adminApi.post(`/admin/recipes/${id}/classify`, input),

  listRecalls: (filters: RecallListFilters = {}) =>
    adminApi.get<{
      status: string;
      count: number;
      items: RecallListItem[];
      nextCursor: string | null;
    }>(`/admin/recalls${qs({ ...filters })}`),
  getRecall: (id: string) => adminApi.get<RecallDetail>(`/admin/recalls/${id}`),
  linkRecallProduct: (id: string, glpProductId: string) =>
    adminApi.post(`/admin/recalls/${id}/link`, { glpProductId }),
  unlinkRecallProduct: (id: string, glpProductId: string) =>
    adminApi.post(`/admin/recalls/${id}/unlink`, { glpProductId }),
  closeRecall: (id: string) => adminApi.post(`/admin/recalls/${id}/close`),
  reopenRecall: (id: string) => adminApi.post(`/admin/recalls/${id}/reopen`),

  listUsers: (filters: UserListFilters = {}) =>
    adminApi.get<{
      status: string;
      count: number;
      items: UserListItem[];
      nextCursor: string | null;
    }>(`/admin/users${qs({ ...filters })}`),
  setUserMembership: (glpUserId: string, input: { tier: string; expiresAt: string | null; reason: string }) => adminApi.post<{ tier: string; expiresAt: string | null }>(`/admin/users/${encodeURIComponent(glpUserId)}/membership`, input),
  getUser: (glpUserId: string) =>
    adminApi.get<{ user: UserDetail; cognito: CognitoState | null; membership: MembershipState }>(`/admin/users/${glpUserId}`),
  createUser: (input: CreateUserInput) => adminApi.post('/admin/users', input),
  updateUser: (glpUserId: string, input: UpdateUserInput) =>
    adminApi.patch(`/admin/users/${glpUserId}`, input),
  setUserRole: (glpUserId: string, role: PortalRole) =>
    adminApi.post(`/admin/users/${glpUserId}/role`, { role }),
  disableUser: (glpUserId: string) => adminApi.post(`/admin/users/${glpUserId}/disable`),
  enableUser: (glpUserId: string) => adminApi.post(`/admin/users/${glpUserId}/enable`),
  deleteUser: (glpUserId: string, hard = false, reason?: string) =>
    adminApi.post(`/admin/users/${glpUserId}/delete${qs({ hard: hard ? 'true' : undefined, reason })}`),
  restoreUser: (glpUserId: string) => adminApi.post(`/admin/users/${glpUserId}/restore`),

  // ── FAQ management (faq:edit) ──
  listFaqs: (filters: FaqListFilters = {}) =>
    adminApi.get<{ count: number; items: FaqItem[]; topics: string[] }>(
      `/admin/faqs${qs({ ...filters })}`,
    ),
  createFaq: (input: CreateFaqInput) => adminApi.post<{ faq: FaqItem }>('/admin/faqs', input),
  updateFaq: (id: string, input: UpdateFaqInput) =>
    adminApi.patch<{ faq: FaqItem }>(`/admin/faqs/${id}`, input),
  deleteFaq: (id: string) => adminApi.post(`/admin/faqs/${id}/delete`),

  // ── Support / contact requests (support:read | support:resolve) ──
  listSupportRequests: (filters: SupportListFilters = {}) =>
    adminApi.get<{
      status: string;
      count: number;
      items: SupportRequestItem[];
      nextCursor: string | null;
      topics: string[];
    }>(`/admin/support-requests${qs({ ...filters })}`),
  getSupportRequest: (id: string) =>
    adminApi.get<{ request: SupportRequestItem; submitter: SupportSubmitter | null }>(
      `/admin/support-requests/${id}`,
    ),
  answerSupportRequest: (id: string, answer: string, resolve = true) =>
    adminApi.post<{ request: SupportRequestItem }>(`/admin/support-requests/${id}/answer`, {
      answer,
      resolve,
    }),
  resolveSupportRequest: (id: string) => adminApi.post(`/admin/support-requests/${id}/resolve`),
  reopenSupportRequest: (id: string) => adminApi.post(`/admin/support-requests/${id}/reopen`),

  // ── Mailing list — marketing subscribers (mailinglist:read) ──
  mailingListSummary: () => adminApi.get<MailingSummary>('/admin/mailing-list/summary'),
  listSubscribers: (filters: SubscriberListFilters = {}) =>
    adminApi.get<{
      channel: string;
      count: number;
      items: SubscriberItem[];
      nextCursor: string | null;
    }>(`/admin/mailing-list${qs({ ...filters })}`),

  // ── Staff management (staff:manage = Admin-app Admin level) ──
  listStaff: (filters: { q?: string; status?: 'active' | 'disabled' | 'all' } = {}) =>
    adminApi.get<{ count: number; items: StaffMember[] }>(`/admin/staff${qs({ ...filters })}`),
  // ─── Product-image sourcing queue ───────────────────────────────
  // 🛑 submitProductImageSource sends a URL we will FETCH, never render.
  // The server stores it in sourceImageUrl; productImageRehost downloads it
  // and publishes our own copy before anything appears to a user.
  listProductImageQueue: (state: ProductImageQueueState, q?: string, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ state, limit: String(limit), offset: String(offset) });
    if (q) params.set('q', q);
    return adminApi.get<{
      items: ProductImageQueueItem[];
      total: number;
      limit: number;
      offset: number;
    }>(`/admin/product-images?${params.toString()}`);
  },
  productImageQueueStats: () =>
    adminApi.get<ProductImageQueueStats>('/admin/product-images/stats'),
  submitProductImageSource: (glpProductId: string, sourceImageUrl: string, note?: string, evidenceRevision?: number) =>
    adminApi.post<{ glpProductId?: string; productId?:string; pendingRehost?: boolean; message?: string;revision?:number;proposalState?:'pending_validation' }>(
      evidenceRevision?`/admin/products/${encodeURIComponent(glpProductId)}/evidence/image-candidates`:`/admin/product-images/${encodeURIComponent(glpProductId)}/source`,
      evidenceRevision?{expectedRevision:evidenceRevision,proposal:{sourceImageUrl,...(note?{note}:{})}}:{ sourceImageUrl, ...(note ? { note } : {}) },
    ),
  skipProductImage: (glpProductId: string, reason: string) =>
    adminApi.post<{ glpProductId: string; imageFindState: string }>(
      `/admin/product-images/${glpProductId}/skip`,
      { reason },
    ),
  assignStaff: (input: AssignStaffInput) =>
    adminApi.post<{ status: string; staff: StaffMember }>('/admin/staff', input),
  updateStaff: (id: string, input: UpdateStaffInput) =>
    adminApi.patch<{ status: string; staff: StaffMember }>(`/admin/staff/${id}`, input),
  removeStaff: (id: string) => adminApi.post<{ status: string; id: string }>(`/admin/staff/${id}/remove`),
};
